// FormSubmission.js
import { stairDetails } from "./stairDetailsConfig";
import { requestOTP, handleOTPSubmission, initializeOTPHandlers } from './OTPHandler';
import { validatePhoneNumber, normalizePhoneNumber } from './phoneValidation';
const apiUrl = 'https://gnogztrnq1.execute-api.ap-southeast-2.amazonaws.com/dev';
let storedFormData = null; 

// Helper function to show/hide loading spinner
function toggleLoadingSpinner(show) {
    const loadingSpinner = document.getElementById('loading-spinner');
    if (show) {
        loadingSpinner.classList.remove('hidden');
    } else {
        loadingSpinner.classList.add('hidden');
    }
}

function isMobileDevice() {
    return window.innerWidth < 768; // Common breakpoint for tablets/mobile
}

// Helper function to show notifications
function showNotification(message, type) {
    const notification = document.createElement('div');
    notification.innerHTML = message;
    notification.classList.add(`notification-${type}`);
    document.body.appendChild(notification);

    setTimeout(() => {
        notification.remove();
    }, 3000);
}

// Helper function to format dates
function formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

// Helper function to convert blob to base64
async function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Access form submission handler
// Update the submitAccessForm function
// Access form submission handler
export async function submitAccessForm(event) {
    event.preventDefault();
    event.stopPropagation();
    
    try {
        console.log("Starting submitAccessForm");
        const form = document.getElementById('access-form');
        const formData = {
            name: form.querySelector('#name').value,
            email: form.querySelector('#email').value,
            phone: form.querySelector('#phone').value.trim(),
            siteAddress: form.querySelector('#siteAddress').value
        };

        const otpSection = document.getElementById('otp-section');
        console.log("OTP section hidden?", otpSection.classList.contains('hidden'));
        
        if (otpSection.classList.contains('hidden')) {
            console.log("First submission - requesting OTP");
            toggleLoadingSpinner(true);
            try {
                const otpResponse = await requestOTP(formData.phone);
                console.log("OTP Response received:", otpResponse);
                if (otpResponse.formattedPhone) {
                    formData.phone = otpResponse.formattedPhone;
                }
                storedFormData = formData;
                // Hide the form and show OTP section
                form.style.display = 'none';
                otpSection.classList.remove('hidden');
                showNotification('Verification code sent to your phone', 'info');
                
                // Set up verify button handler
                const verifyBtn = document.getElementById('verify-otp-btn');
                verifyBtn.onclick = async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await handleVerifyClick(formData.phone);
                };
            } finally {
                toggleLoadingSpinner(false);
            }
            return;
        }
    } catch (error) {
        console.error('Error in form submission:', error);
        showNotification(error.message, 'error');
    }
}

async function handleVerifyClick(phone) {
    toggleLoadingSpinner(true);
    
    try {
        const otpInput = document.getElementById('otp-input');
        if (!otpInput || !otpInput.value) {
            throw new Error('Please enter the verification code');
        }

        console.log('Attempting to verify OTP...');
        const otpVerified = await handleOTPSubmission(phone, otpInput.value);
        
        if (!otpVerified) {
            throw new Error('Verification failed');
        }

        // Only proceed if we have stored form data
        if (!storedFormData) {
            throw new Error('Form data not found. Please try again.');
        }

        console.log('OTP verified, submitting form data...');
        const response = await fetch(`${apiUrl}/submit-form`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(storedFormData)
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.error || 'Failed to submit form');
        }

        // Success! Save data and close form
        localStorage.setItem('userEmail', storedFormData.email);
        localStorage.setItem('lastAccessDate', new Date().getTime());
        document.getElementById('form-overlay').classList.add('hidden');
        document.getElementById('price').classList.remove('price-blur');
        document.getElementById('price-overlay').style.display = 'none';
        
        // Show submit button for new authenticated users
        const submitQuoteBtn = document.getElementById('submitQuoteBtn');
        if (submitQuoteBtn) {
            submitQuoteBtn.classList.remove('hidden');
        }
        
        const successMessage = isMobileDevice() 
            ? 'Use desktop for best experience' 
            : 'Access granted successfully!';
        showNotification(successMessage, 'success');
        
    } catch (error) {
        console.error('Error in verification:', error);
        
        // Handle specific OTP expiration
        if (error.message === 'EXPIRED_OTP') {
            const otpSection = document.getElementById('otp-section');
            otpSection.classList.add('hidden');
            document.getElementById('otp-input').value = '';
            // Re-request OTP automatically
            try {
                await requestOTP(phone);
                otpSection.classList.remove('hidden');
                showNotification('Previous code expired. New verification code sent!', 'info');
            } catch (retryError) {
                showNotification('Failed to send new code. Please try again.', 'error');
            }
        } else {
            showNotification(error.message, 'error');
        }
    } finally {
        toggleLoadingSpinner(false);
    }
}

// Main quote submission function - called from InputListeners
export async function submitForm(mainSceneSetup) {
    console.log('submitForm called with mainSceneSetup:', mainSceneSetup);
    toggleLoadingSpinner(true);

    try {
        // Check user access
        const userEmail = localStorage.getItem('userEmail');
        if (!userEmail) {
            document.getElementById('form-overlay').classList.remove('hidden');
            return;
        }

        // Get user info from DynamoDB
        const userResponse = await fetch(`${apiUrl}/get-user-info?email=${userEmail}`);
        const userInfo = await userResponse.json();

        if (!userResponse.ok) {
            if (userResponse.status === 401) {
                localStorage.removeItem('userEmail');
                localStorage.removeItem('lastAccessDate');
                document.getElementById('form-overlay').classList.remove('hidden');
                return;
            }
            throw new Error('Failed to retrieve user information');
        }

        // Get calculator data
        const calculatorData = mainSceneSetup.getCalculatorData();
        console.log('Calculator data:', calculatorData);

        // Get stair type
        const stairType = document.querySelector('input[name="cantilever-option"]:checked').value;
        
        // Set tread dimensions label and value based on stair type
        const treadDimensions = stairType === 'Spiral' ? 'DIAMETER' : 'TREAD WIDTH (mm)';
        const dimensionValue = stairType === 'Spiral' ? '1990 mm' : `${calculatorData.width} mm`;
        const selectedDetails = stairDetails[stairType] || stairDetails['central-stringer'];
        const dimensionValue2 = stairType === 'Spiral' ? '940 mm' : `${calculatorData.width} mm`;

        // Capture screenshot
        const screenshotBlob = await mainSceneSetup.captureScreenshot();
        const base64Screenshot = await blobToBase64(screenshotBlob);

        // Calculate prices
        const unitPrice = mainSceneSetup.calculatedPrice;
        const GST = (unitPrice * 0.15).toFixed(2);
        const totalInclGST = (parseFloat(unitPrice) + parseFloat(GST)).toFixed(2);

        // Prepare quote data
        const quoteData = {
            name: userInfo.name,
            email: userInfo.email,
            phone: userInfo.phone,
            siteAddress: userInfo.siteAddress,
            date: formatDate(new Date()),
            expiryDate: formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
            staircaseType1: calculatorData.staircaseType,
            staircaseType: calculatorData.staircaseType,
            staircaseAttachment1: document.querySelector('input[name="cantilever-option"]:checked').value,
            staircaseAttachment: document.querySelector('input[name="cantilever-option"]:checked').value,
            numberOfTreads: calculatorData.numberOfTreads,
            numberOfTreads1: calculatorData.numberOfTreads,
            width: dimensionValue,
            treadWidth: dimensionValue2,
            treadDimensions: treadDimensions,
            details: selectedDetails,
            unitPrice1: unitPrice.toFixed(2),
            unitPrice2: unitPrice.toFixed(2),
            unitPrice3: unitPrice.toFixed(2),
            GST,
            totalInclGST,
            screenshot: base64Screenshot
        };

        // Send quote email
        const response = await fetch(`${apiUrl}/send-email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(quoteData)
        });

        if (!response.ok) {
            throw new Error('Failed to send quote email');
        }

        showNotification('Quote has been sent to your email!', 'success');
    } catch (error) {
        console.error('Error generating quote:', error);
        showNotification('Failed to generate quote. Please try again.', 'error');
    } finally {
        toggleLoadingSpinner(false);
    }
}

// Initialize access form handling when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
    const accessForm = document.getElementById('access-form');
    if (accessForm) {
        accessForm.addEventListener('submit', submitAccessForm);
    }
});